import React from 'react';
import { Folder, Clapperboard, FileText, Video } from 'lucide-react';

import { Button, Loader } from '@/components/ui';
import { useVideoDashboardStats } from '@/lib/api/hooks/video';

const formatNumber = (value: number): string => new Intl.NumberFormat().format(value);

const toGb = (value: number): number => value / 1_000_000;

const getStorageRatio = (used: number, total: number): number => {
	if (!total) return 0;
	return Math.min(100, Math.max(0, (used / total) * 100));
};

const StatCard = ({
	icon,
	title,
	value,
	subtitle,
	iconBgClass,
	iconClass,
	children,
}: {
	icon: React.ReactNode;
	title: string;
	value: string;
	subtitle: string;
	iconBgClass: string;
	iconClass?: string;
	children?: React.ReactNode;
}) => (
	<div className="rounded-xl border border-gray-200 bg-white px-5 py-5">
		<div className="grid grid-cols-[auto_1fr] items-center gap-3">
			<div className={`flex h-14 w-14 items-center justify-center rounded-md ${iconBgClass}`}>
				<span className={iconClass}>{icon}</span>
			</div>
			<div>
				<p className="text-base font-medium text-gray-500">{title}</p>
				<p className="mt-1 text-[2.05rem] leading-none font-semibold text-gray-900">{value}</p>
				<p className="text-sm text-gray-500 mt-1">{subtitle}</p>
			</div>
		</div>
		{children}
	</div>
);

interface StatConfig {
	key: string;
	title: string;
	icon: React.ReactNode;
	iconBgClass: string;
	getValue: (values: {
		videoFolders: number;
		publishedVideo: number;
		draftPhotos: number;
		totalPhotos: number;
		storageUsedGb: number;
	}) => string;
	getSubtitle: (values: { storageTotalGb: number; storagePercent: number }) => string;
	renderExtra?: (values: { storagePercent: number }) => React.ReactNode;
}

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'videoFolders',
		title: 'Total Folders',
		icon: <Folder strokeWidth={1.25} className="h-8 w-8 text-[#8B203A]" />,
		iconBgClass: 'bg-[#FDF2F4]',
		getValue: (values) => formatNumber(values.videoFolders),
		getSubtitle: () => 'All folders',
	},
	{
		key: 'publishedVideo',
		title: 'Published Albums',
		icon: <Clapperboard strokeWidth={1.25} className="h-8 w-8 text-[#0F8A47]" />,
		iconBgClass: 'bg-[#E8F6EE]',
		getValue: (values) => formatNumber(values.publishedVideo),
		getSubtitle: () => 'Public on website',
	},
	{
		key: 'draftPhotos',
		title: 'Draft Albums',
		icon: <FileText strokeWidth={1.25} className="h-8 w-8 text-[#B86A00]" />,
		iconBgClass: 'bg-[#FDF3E6]',
		getValue: (values) => formatNumber(values.draftPhotos),
		getSubtitle: () => 'Not published',
	},
	{
		key: 'totalPhotos',
		title: 'Total Videos',
		icon: <Video strokeWidth={1.25} className="h-8 w-8 text-[#6D28D9]" />,
		iconBgClass: 'bg-[#F5F0FF]',
		getValue: (values) => formatNumber(values.totalPhotos),
		getSubtitle: () => 'Across all folders',
	}
	// {
	// 	key: 'storageUsed',
	// 	title: 'Storage Used',
	// 	icon: <Cloud strokeWidth={1.25} className="h-8 w-8 text-[#2563EB]" />,
	// 	iconBgClass: 'bg-[#EEF4FF]',
	// 	getValue: (values) => `${values.storageUsedGb.toFixed(1)} GB`,
	// 	getSubtitle: (values) => `of ${values.storageTotalGb.toFixed(0)} GB (${values.storagePercent.toFixed(1)}%)`,
	// 	renderExtra: (values) => (
	// 		<div className="mt-3 h-2 w-full rounded-full bg-gray-100">
	// 			<div className="h-2 rounded-full bg-[#3B82F6]" style={{ width: `${values.storagePercent}%` }} />
	// 		</div>
	// 	),
	// },
];

const VideoStats: React.FC = () => {
	const { data, isLoading, isError, refetch, isFetching } = useVideoDashboardStats();

	const stats = {
		videoFolders: data?.videoFolders ?? 0,
		publishedVideo: data?.publishedVideo ?? 0,
		draftPhotos: data?.draftPhotos ?? 0,
		totalPhotos: data?.totalPhotos ?? 0,
		storageUsed: data?.storage?.used ?? 0,
		storageTotal: data?.storage?.total ?? 0,
	};

	const storageUsedGb = toGb(stats.storageUsed);
	const storageTotalGb = toGb(stats.storageTotal);
	const storagePercent = getStorageRatio(stats.storageUsed, stats.storageTotal);

	const values = {
		videoFolders: stats.videoFolders,
		publishedVideo: stats.publishedVideo,
		draftPhotos: stats.draftPhotos,
		totalPhotos: stats.totalPhotos,
		storageUsedGb,
		storageTotalGb,
		storagePercent,
	};

	if (isError) {
		return (
			<div className="flex w-full flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white py-10">
				<p className="text-sm text-gray-600">Error loading video statistics.</p>
				<Button type="button" onClick={() => refetch()} loading={isFetching}>
					Retry
				</Button>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-10">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		);
	}

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-3 2xl:grid-cols-5">
			{STATS_CONFIG.map((stat) => (
				<StatCard
					key={stat.key}
					icon={stat.icon}
					title={stat.title}
					value={stat.getValue(values)}
					subtitle={stat.getSubtitle(values)}
					iconBgClass={stat.iconBgClass}
				>
					{stat.renderExtra?.(values)}
				</StatCard>
			))}
		</div>
	);
};

export default VideoStats;
